<template>
  <v-container class="mt-0">
    <v-row>
      <v-col align-center>
        <div class="text-center mb-2">
          <span class="text-h6">{{ viewName }}</span>
          <span class="">&nbsp;({{ recordName }}) - Full range is</span>
          <span class="font-weight-medium">
            {{ formatJustDatestamp(timestamps[0]) }}
          </span>
          <span v-if="timestamps.length > 1">
            <span>to</span>
            <span class="font-weight-medium">
              {{ formatJustDatestamp(timestamps[timestamps.length - 1]) }}
            </span>
          </span>
        </div>
      </v-col>
    </v-row>
    <v-simple-table class="fixed-table">
      <template v-slot:default>
        <thead>
          <tr>
            <th
              v-if="!editMode"
              class="body-1 text-right overflow-hidden"
              key="left"
            >
              <v-tooltip max-width="30vw" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    @click="scrollTable(-numberOfDatesToScroll)"
                    v-bind="attrs"
                    v-on="on"
                    color="info"
                    icon
                    small
                    :disabled="beginIndex <= 0"
                  >
                    <v-icon small class="leftArrow">mdi-navigation</v-icon>
                  </v-btn>
                </template>
                <span>Show {{ numberOfDatesToScroll }} earlier values</span>
              </v-tooltip>
            </th>
            <th
              v-else
              class="body-1 pr-4 text-right overflow-hidden width-25"
              key="left"
            >
              Category
            </th>
            <th
              class="text-center"
              :class="{ 'd-none': editMode }"
              v-for="(item, i) in headerArray"
              :key="i"
            >
              <transition :name="direction">
                <div :key="item">
                  <div class="font-weight-medium">
                    {{ formatJustDatestamp(item) }}
                  </div>
                  <div class="font-weight-light">
                    {{ formatJustTimestamp(item) }}
                  </div>
                </div>
              </transition>
            </th>
            <th
              v-if="!editMode"
              class="body-1 text-left overflow-hidden"
              key="right"
            >
              <v-tooltip max-width="30vw" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    @click="scrollTable(numberOfDatesToScroll)"
                    v-bind="attrs"
                    v-on="on"
                    color="info"
                    icon
                    small
                    :disabled="endIndex >= timestamps.length - 1"
                  >
                    <v-icon small class="rightArrow">mdi-navigation</v-icon>
                  </v-btn>
                </template>
                <span>Show {{ numberOfDatesToScroll }} later values</span>
              </v-tooltip>
            </th>
            <th v-else class="text-center">
              <div>
                <div class="font-weight-medium">
                  {{ formatJustDatestamp(creationDateTime) }}
                </div>
                <div class="font-weight-light">
                  {{ formatJustTimestamp(creationDateTime) }}
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(value, key) in nodeGroups" :key="`parent-${key}`">
            <td v-if="value.length" v-bind:colspan="cellColSpan">
              <v-simple-table width="100%">
                <thead>
                  <th
                    class="blue darken-1 white--text text-left pl-3 inner-cell"
                    :class="{ 'width-25': editMode }"
                    v-bind:colspan="headerColSpan"
                  >
                    <span class="text-body-1">
                      {{ getGroupTitle(key) }}
                    </span>
                  </th>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in value" :key="index">
                    <v-tooltip
                      max-width="30vw"
                      :disabled="!item.meta.question"
                      bottom
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <td
                          v-bind="attrs"
                          v-on="on"
                          class="text-right pr-4 overflow-hidden inner-cell"
                          :class="{ 'width-25': editMode }"
                        >
                          {{ item.name }}
                        </td>
                      </template>
                      <span>{{ item.meta.question }}</span>
                    </v-tooltip>

                    <td
                      class="text-center inner-cell"
                      :class="{ 'd-none': editMode }"
                      v-for="(cell, i) in bodyArray"
                      :key="i"
                    >
                      <transition :name="direction">
                        <div
                          :class="{ isEmpty: !getValueForNode(cell, item) }"
                          :key="`${displayValue(cell, item)}-${i}`"
                        >
                          <span
                            v-for="(piece, j) in getValuesForThisNode(
                              cell,
                              item
                            )"
                            :key="`cell-value-${i}-${j}`"
                          >
                            <div>{{ displayIndividualValue(piece) }}</div>
                          </span>
                        </div>
                      </transition>
                    </td>
                    <td
                      v-if="!editMode"
                      class="overflow-hidden text-left pl-4"
                    ></td>
                    <td v-else>
                      <TypeFactory
                        :node="item"
                        :editMode="true"
                        :editIndex="item.value ? item.value.length - 1 : -1"
                        :selectedRecord="selectedRecord"
                        :isRoot="false"
                        :createMode="false"
                        class="mx-4"
                        v-on:resetform="updateConditionals()"
                      ></TypeFactory>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
  </v-container>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";

import { GetRecordConditionalHiddenIds } from "../../../utils/conditionalParser.js";

import moment from "moment";
import TypeFactory from "../TypeFactory.vue";
import { treeWalker } from "../../../utils/treeUtils";

const numberOfDatesInTable = 5;

export default {
  name: "EventDisplay",
  data() {
    return {
      timestamps: [],
      beginIndex: 0,
      endIndex: 0,
      valueMap: new Map(),
      numberOfDatesToScroll: 3,
      direction: "none",
      eventNodes: [],
      nodeGroups: {},
      nodesToHide: [],
    };
  },
  props: {
    isEmbedded: Boolean,
    creationDateTime: String,
    selectedRecord: Object,
    editMode: Boolean,
    type: String,
  },
  created() {},
  mounted() {
    this.$root.$on("updatecondnodes", () => {
      this.updateConditionals();
    });
  },

  computed: {
    ...mapState({
      recordName: (state) => {
        if (state.records.currentlySelectedTab) {
          return state.records.currentlySelectedTab.recordName;
        } else {
          return "Loading...";
        }
      },
      viewName: (state) => {
        if (state.records.currentlySelectedTab) {
          return state.records.currentlySelectedTab.typeSpecificPayload;
        } else {
          return "Loading...";
        }
      },
      // newTimestamp: (state) => {
      //   if (state.records.currentlySelectedTab) {
      //     return new Date(state.records.currentlySelectedTab.datetime);
      //   } else {
      //     return null;
      //   }
      // },
    }),
    ...mapGetters({
      events: "getEventsGroupByTab",
      groups: "getGroupByTab",
      rootNode: "getRecordItem",
    }),
    headerArray() {
      return this.timestamps.slice(this.beginIndex, this.endIndex + 1);
    },
    bodyArray() {
      const times = this.timestamps
        .slice(this.beginIndex, this.endIndex + 1)
        .map((time) => {
          return this.valueMap.get(time);
        });
      return times;
    },

    cellColSpan() {
      return this.editMode ? 2 : numberOfDatesInTable + 2;
    },
    headerColSpan() {
      return this.editMode ? 1 : numberOfDatesInTable + 2;
    },
  },
  methods: {
    ...mapActions(["currentlySelectedTab", "getRecordItemOnDemand"]),

    buildTimestampArray() {
      // rebuild the array of dates and the value map for each date
      this.timestamps = [];

      this.eventNodes.forEach((node) => {
        const name = node.name;
        const units = node.meta.uom;
        const type = node.meta.type;
        const separator = node.meta.separator || node.meta.spacer || ", ";
        const _id = node._id;
        if (Array.isArray(node.value)) {
          node.value.forEach((value) => {
            const obj = {
              values: value.val,
              timestamp: value.eventTimestamp,
              name,
              units,
              type,
              separator,
              _id,
            };

            if (this.timestamps.indexOf(value.eventTimestamp) === -1) {
              this.timestamps.push(value.eventTimestamp);
              this.valueMap.set(value.eventTimestamp, [obj]);
            } else {
              const existingVals = this.valueMap.get(value.eventTimestamp);
              existingVals.push(obj);
              this.valueMap.set(value.eventTimestamp, existingVals);
            }
          });
        } else if (node.value) {
          const obj = {
            name: node.name,
            type: "np-date",
            values: [node.value],
            timestamp: node.meta.created,
          };

          if (this.timestamps.indexOf(obj.timestamp) === -1) {
            this.timestamps.push(obj.timestamp);
            this.valueMap.set(obj.timestamp, [obj]);
          } else {
            const existingVals = this.valueMap.get(obj.timestamp);
            existingVals.push(obj);
            this.valueMap.set(obj.timestamp, existingVals);
          }
        }
      });
      // Purge all timestamps where there are no value entries for any of the nodes
      const testTimestamps = this.timestamps.concat();
      this.timestamps = testTimestamps.filter((stamp) => {
        const items = this.valueMap.get(stamp);
        const hasNoData = items.every((item) => {
          if (!Array.isArray(item.values)) return;
          item.values.every((value) => {
            return value === null || value === "";
          });
        });
        if (hasNoData) {
          this.valueMap.delete(stamp);
        }
        return !hasNoData;
      });
      // Now sort the array
      this.timestamps.sort();
      // Update end index to the new last entry
      this.endIndex = this.timestamps.length - 1;
      // Update begin index to be numberOfDatesInTable entries before end index or beginning of the array
      this.beginIndex = Math.max(
        0,
        this.timestamps.length - numberOfDatesInTable
      );
    },
    buildEventNodeList(group) {
      this.nodesToHide = GetRecordConditionalHiddenIds(this.selectedRecord);

      this.eventNodes = [];
      this.nodeGroups = {};

      const items =
        this.type === "event" ? this.events[group] : this.groups[group];

      items.forEach((node) => {
        const foundNode = treeWalker(this.rootNode, node._id);

        this.eventNodes.push(foundNode);

        const parent = foundNode.parent;
        if (parent) {
          if (this.nodeGroups[parent]) {
            const children = this.nodeGroups[parent];
            children.push(foundNode);
            this.$set(this.nodeGroups, parent, children);
          } else {
            this.$set(this.nodeGroups, parent, [foundNode]);
          }
        }
      });
      // console.log(this.eventNodes[0].value.at(-1));
      // console.log(this.eventNodes[0].value.at(-2));
      this.buildTimestampArray();
      // if (this.editMode) {
      //   this.addNewValues(this.eventNodes, this.newTimestamp);
      // }
    },
    formatJustTimestamp(timestamp) {
      return moment(timestamp).format("h:mm a");
    },
    formatJustDatestamp(timestamp) {
      return moment(timestamp).format("MMM Do, YYYY");
    },
    displayValue(vals, node) {
      const filtered = this.getValuesForThisNode(vals, node);
      if (filtered) {
        return filtered
          .map((item) => {
            let result = this.displayIndividualValue(item);
            if (filtered.length > 1) {
              result += ` (${this.formatJustTimestamp(item.timestamp)})`;
            }
            return result;
          })
          .join("\n");
      }
    },
    displayIndividualValue(result) {
      if (result) {
        const units = result.units ? result.units : "";
        if (result.values?.some((v) => v !== null)) {
          if (result.values.length === 1) {
            return `${result.values} ${units}`;
          } else {
            return `${result.values.join(result.separator)} ${units}`;
          }
        } else {
          return "";
        }
      } else {
        return "";
      }
    },
    getValuesForThisNode(vals, node) {
      return vals.filter((val) => val._id === node._id);
    },
    getValueForNode(vals, node) {
      return vals.find((val) => val._id === node._id);
    },
    scrollTable(days) {
      this.direction = days < 0 ? "slide-right" : "slide-left";
      this.beginIndex += days;
      this.endIndex += days;
      if (this.beginIndex < 0) {
        this.beginIndex = 0;
        this.endIndex = Math.min(
          this.timestamps.length - 1,
          numberOfDatesInTable - 1
        );
      }
      if (this.endIndex >= this.timestamps.length - 1) {
        this.endIndex = this.timestamps.length - 1;
        this.beginIndex = this.timestamps.length - numberOfDatesInTable;
      }
    },
    haveValuesBeenEntered(node) {
      if (node.value.length) {
        return (
          node.value[node.value.length - 1].eventTimestamp ===
          this.newTimestamp.toISOString()
        );
      } else {
        return false;
      }
    },
    addNewValues(nodeList, timestamp) {
      const now = new Date().toISOString();
      nodeList.forEach((node) => {
        // Make sure this is a periodic node and we haven't already added this entry
        if (node.meta.periodic && !this.haveValuesBeenEntered(node)) {
          // If this node uses arrays of more than one element, create the proper length array
          const newValueLen = node.meta.value_name
            ? node.meta.value_name.length
            : 1;
          const newValue = {
            event: this.viewName,
            eventTimestamp: timestamp.toISOString(),
            timestamp: now,
            val: new Array(newValueLen),
          };
          if (node.meta.min) {
            newValue.val.fill(node.meta.min);
          }
          node.value.push(newValue);
        }
      });
    },
    checkIfNodeShouldBeDisplayed(nodeId) {
      return !this.nodesToHide.includes(nodeId);
    },
    checkifNodeGroupChildrenShouldBeDisplayed(node) {
      this.nodesToHide;

      node.forEach((child) => {
        if (this.nodesToHide.includes(child._id)) {
          node.splice(node.indexOf(child), 1);
        }
      });
    },
    updateConditionals() {
      this.nodesToHide = GetRecordConditionalHiddenIds(this.selectedRecord);
      this.buildEventNodeList(this.viewName);
    },
    getGroupTitle(parentId) {
      if (parentId !== this.rootNode._id) {
        // Walk the tree backward until we hit the rootNode, which shouldn't have a valid parent ID
        let ancestors = [];
        let currentNodeParent = parentId;
        while (currentNodeParent) {
          const currentNode = treeWalker(this.rootNode, currentNodeParent);
          if (currentNode && currentNode._id === this.rootNode._id) {
            break;
          }
          ancestors.push(currentNode.name);
          currentNodeParent = currentNode.parent;
        }
        return ancestors.reverse().join(" → ");
      }
      return this.selectedRecord.name;
    },
  },
  watch: {
    selectedRecord: {
      handler: function() {
        this.nodesToHide = GetRecordConditionalHiddenIds(this.selectedRecord);
        this.buildEventNodeList(this.viewName);
      },
      immediate: true,
    },
    nodesToHide: {
      handler: function() {
        this.eventNodes.forEach((node) => {
          if (!this.checkIfNodeShouldBeDisplayed(node._id)) {
            this.eventNodes.splice(this.eventNodes.indexOf(node), 1);
          }
        });
        for (let node in this.nodeGroups) {
          this.checkifNodeGroupChildrenShouldBeDisplayed(this.nodeGroups[node]);
        }
      },
    },
    events: {
      deep: true,
      immediate: true,
      handler: function(newVal) {
        this.buildEventNodeList(this.viewName);
      },
    },
    editMode: function(newVal, oldVal) {
      if (newVal !== oldVal) {
        if (newVal) {
          // this.addNewValues(this.eventNodes, this.newTimestamp);
        } else {
          this.buildEventNodeList(this.viewName);
        }
      }
    },
  },
  components: { TypeFactory },
};
</script>

<style scoped lang="scss">
.v-data-table {
  border: none;

  & > .v-data-table__wrapper > table th,
  & > .v-data-table__wrapper > table td {
    border-right: 1px solid rgba(0, 0, 0, 0.12);
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    padding: 0;
    position: relative;
  }

  & table th:last-child {
    border-right: none;
  }
  tr {
    background-color: #fff;
  }
}
.leftArrow {
  display: inline-block;
  transform: rotate(-90deg);
}
.rightArrow {
  display: inline-block;
  transform: rotate(90deg);
}
.isEmpty {
  display: block;
  background-color: #f7f7f7;
  height: 100%;
  width: 100%;
}

.slide-left-enter-active,
.slide-right-enter-active {
  transition: all 0.3s ease;
}
.slide-left-leave-active,
.slide-right-leave-active {
  transition: all 0s;
  position: absolute;
  z-index: 2;
}
.slide-left-enter,
.slide-right-leave-to {
  // Make sure this is 100% * number of columns we scroll
  transform: translateX(300%);
}
.slide-right-enter,
.slide-left-leave-to {
  // Make sure this is 100% * number of columns we scroll
  transform: translateX(-300%);
}

.slide-right-leave-to,
.slide-left-leave-to {
  display: none;
}
</style>
<style lang="scss">
.fixed-table table {
  table-layout: fixed;
  border-collapse: collapse;
  overflow: hidden;
}
.overflow-hidden {
  overflow: hidden;
  background-color: #fff;
  z-index: 3;
  height: 100%;
}
.width-25 {
  width: 25%;
}

.inner-cell {
  padding: 4px;
}
</style>
